import React from 'react';
import homeConsumerImage from '../../static/consumer_image/home_consumer_img.png';
import portableConsumerImage from '../../static/consumer_image/portable_consumer_img3.png';

import AnnotatedImage, {
    AutoImageProps,
    processLabelToComponentName,
    useResponsiveMargins,
} from '../automotivePage/CarImageComponent';
import {
    ApplicationsConsumerHomeConsumerContent,
    ApplicationsConsumerPortableConsumerContent,
} from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';

interface HomeConsumerProps extends AutoImageProps {
    cmsContent: ApplicationsConsumerHomeConsumerContent;
}
interface PortableConsumerProps extends AutoImageProps {
    cmsContent: ApplicationsConsumerPortableConsumerContent;
}

const HomeConsumer: React.FC<HomeConsumerProps> = ({ onAnnotationClick, cmsContent }) => {
    const responsiveMargins = useResponsiveMargins('homeConsumer');
    const t = cmsContent.imageMapLinkTexts;

    const labelToComponentMap = Object.entries(t).reduce(
        (map, [key, value]) => {
            map[value.content] = key.toLowerCase(); // ex: "Gaming" ➜ "gaming"
            return map;
        },
        {} as Record<string, string>
    );

    const annotations = [
        {
            label: t.homeAppliances.content,
            pointX: 120,
            pointY: 520,
            labelX: 140,
            labelY: 300,
            lineStartAdjust: 9,
            lineToLabelGap: -10,
            lineToPointGap: -30,
        },
        {
            label: t.iot.content,
            pointX: 440,
            pointY: 290,
            labelX: 340,
            labelY: 160,
            lineStartAdjust: 2,
            lineToLabelGap: -10,
            lineToPointGap: -30,
        },
        {
            label: t.homeTheater.content,
            pointX: 490,
            pointY: 510,
            labelX: 510,
            labelY: 55,
            lineStartAdjust: 9,
            lineToLabelGap: -15,
            lineToPointGap: -30,
        },
        {
            label: t.portableElectronics.content,
            pointX: 720,
            pointY: 520,
            labelX: 745,
            labelY: 19,
            lineStartAdjust: 9,
            lineToLabelGap: -15,
            lineToPointGap: -30,
        },
        {
            label: t.powerTools.content,
            pointX: 790,
            pointY: 575,
            labelX: 980,
            labelY: 870,
            lineStartAdjust: 6,
            lineToLabelGap: -5,
            lineToPointGap: -10,
        },
        {
            label: t.gaming.content,
            pointX: 275,
            pointY: 620,
            labelX: 160,
            labelY: 825,
            lineStartAdjust: 11,
            lineToLabelGap: 2,
            lineToPointGap: -10,
        },
        {
            label: t.peripheralsAndPrinters.content,
            pointX: 370,
            pointY: 780,
            labelX: 497,
            labelY: 880,
            lineStartAdjust: 15,
            lineToLabelGap: 2,
            lineToPointGap: -20,
        },
    ];

    return (
        <AnnotatedImage
            imageSrc={homeConsumerImage}
            annotations={annotations}
            onAnnotationClick={(label) => {
                const componentKey =
                    labelToComponentMap[label] ?? processLabelToComponentName(label);
                onAnnotationClick(componentKey);
            }}
            imageStyle={{
                ...responsiveMargins,
                width: '92%',
                height: 'auto',
            }}
            viewBox={'0 0 1080 920'}
            isDefaultImage
        />
    );
};
const PortableConsumer: React.FC<PortableConsumerProps> = ({ onAnnotationClick, cmsContent }) => {
    const t = cmsContent.imageMapLinkTexts;

    const labelToComponentMap: Record<string, string> = {
        [t.wearables.content]: 'wearables',
        [t.smartphone.content]: 'smartphone',
        [t.tablets.content]: 'tablets',
    };

    const annotations = [
        {
            label: t.wearables.content,
            pointX: 600,
            pointY: 120,
            labelX: 180,
            labelY: 139,
            lineStartAdjust: 14,
            lineEndAdjust: 3,
            lineToLabelGap: 55,
            lineToPointGap: -30,
        },
        {
            label: t.smartphone.content,
            pointX: 570,
            pointY: 270,
            labelX: 120,
            labelY: 290,
            lineStartAdjust: 14,
            lineEndAdjust: 3,
            lineToLabelGap: 70,
            lineToPointGap: -30,
        },
        {
            label: t.tablets.content,
            pointX: 750,
            pointY: 350,
            labelX: 1000,
            labelY: 372,
            lineStartAdjust: 14,
            lineEndAdjust: 3,
            lineToLabelGap: 30,
            lineToPointGap: -30,
        },
    ];

    return (
        <AnnotatedImage
            imageSrc={portableConsumerImage}
            annotations={annotations}
            onAnnotationClick={(label) => {
                const componentKey =
                    labelToComponentMap[label] ?? processLabelToComponentName(label);
                onAnnotationClick(componentKey);
            }}
            imageStyle={{
                width: '99%',
                height: 'auto',
            }}
            viewBox={'0 0 1280 870'}
            isDefaultImage
        />
    );
};

export { HomeConsumer, PortableConsumer };
